import type {AdvancedSearchResponse} from '@api';
import {useFeatureFlagsStore} from '~/stores/feature-flags.store';
import {
    canEnterHighIntentSelectionStep,
    canEnterLowIntentCheckoutStep,
    canResumeCheckoutStep,
    canResumeSelectionStep,
} from './utils/stepsValidations';

export interface StepConfig {
    routeName: string;
    skipTo?: string;
    canEnter: (advancedSearch: AdvancedSearchResponse) => boolean;
    canResume: (advancedSearch: AdvancedSearchResponse) => boolean;
    // If true, this step will be excluded from navigation history/back stack
    excludeFromHistory?: boolean;
}

function useAdvancedSearchWorkflows() {
    const {features} = useFeatureFlagsStore();
    return {
        'low-intent': [
            {
                routeName: 'criteria',
                skipTo: 'checkout',
                canEnter: () => true,
                canResume: () => true,
            },
            {
                routeName: 'selection',
                skipTo: 'checkout',
                canEnter: () => true,
                canResume: (advancedSearch: AdvancedSearchResponse) => canResumeSelectionStep(advancedSearch),
            },
            {
                routeName: 'checkout',
                canEnter: (advancedSearch: AdvancedSearchResponse) => canEnterLowIntentCheckoutStep(advancedSearch),
                canResume: (advancedSearch: AdvancedSearchResponse) => canResumeCheckoutStep(advancedSearch),
            },
        ],
        'high-intent': [
            {
                routeName: 'criteria',
                canEnter: () => true,
                canResume: () => true,
            },
            {
                routeName: 'selection',
                canEnter: (advancedSearch: AdvancedSearchResponse) => canEnterHighIntentSelectionStep(advancedSearch),
                canResume: (advancedSearch: AdvancedSearchResponse) => canResumeSelectionStep(advancedSearch),
            },
            {
                routeName: 'calibration',
                canEnter: () => true,
                canResume: () => false,
            },
        ],
        unified: [
            {
                routeName: 'criteria',
                skipTo: 'checkout',
                canEnter: () => true,
                canResume: () => true,
            },
            ...(features['advanced-search-calibration-page']
                ? [
                      {
                          routeName: 'calibration',
                          canEnter: () => true,
                          canResume: () => false,
                          skipTo: 'checkout',
                      },
                  ]
                : []),
            {
                routeName: 'selection',
                skipTo: 'checkout',
                canEnter: () => true,
                canResume: (advancedSearch: AdvancedSearchResponse) => canResumeSelectionStep(advancedSearch),
            },
            {
                routeName: 'checkout',
                canEnter: (advancedSearch: AdvancedSearchResponse) => canEnterLowIntentCheckoutStep(advancedSearch),
                canResume: (advancedSearch: AdvancedSearchResponse) => canResumeCheckoutStep(advancedSearch),
            },
        ],
    };
}

export type AdvancedSearchWorkflows = ReturnType<typeof useAdvancedSearchWorkflows>;
export type WorkflowKey = keyof AdvancedSearchWorkflows;

export type AdvancedSearchStep<W extends WorkflowKey> = AdvancedSearchWorkflows[W][number]['routeName'];

export function useAdvancedSearchWorkflowSteps(workflowKey: WorkflowKey): StepConfig[] {
    const {features} = useFeatureFlagsStore();
    const workflows = useAdvancedSearchWorkflows();

    if (features['advanced-search-refinement-criteria']) {
        return workflows.unified;
    }

    return workflows[workflowKey];
}
